import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { QueryLoadingState } from '@globalconnect/query';
import { untypedIsEnabled } from '@ip-only/client-features';
import { IpoStringService, IpoTranslatePipe } from '@ip-only/front-end-library';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { Breadcrumb } from '../../shared/breadcrumbs/breadcrumbs-entites';
import { ToastsService } from '../../shared/toast/toast.service';
import { QueryParamService } from '../../shared/util/query-params.service';
import { ScreenSizeClassComponent } from '../../shared/util/screen-size/screen-size.component';
import { SHAREPOINT_EXPORT_ROLES } from '../../user-roles-def/user-roles';
import { ResourcesService } from '../../user/resources.service';
import { Resource } from '../../user/user';
import { UserService } from '../../user/user.service';
import {
  ConstructionDocumentItem,
  ConstructionDocumentsResponse,
  KnownErrorStates,
} from '../entities/construction-documents-entities';
import { ConstructionDocumentsService } from '../service/construction-documents.service';
import { generateSizeString } from './folder-content-helper';
import {
  deleteQueryPath,
  setCurrentPath,
  updateQueryPath,
} from './query-param-path-helper';

@Component({
  selector: 'app-construction-documents',
  templateUrl: './construction-documents.component.html',
  styleUrl: './construction-documents.component.scss',
  standalone: false,
})
export class ConstructionDocumentsComponent
  extends ScreenSizeClassComponent
  implements OnInit, OnChanges, OnDestroy
{
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  @Input() path: string;
  cancelSubs$ = new Subject<void>();
  constructionDocumentsResponse$: Observable<
    QueryLoadingState<
      ConstructionDocumentsResponse,
      HttpErrorResponse,
      ConstructionDocumentsResponse
    >
  >;
  ifs$ = new BehaviorSubject('');
  noIfs$ = new BehaviorSubject(true);
  breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);

  sharepointExport = untypedIsEnabled('sharepointExport');
  modalState = this.constructionDocumentsService.modalState;

  constructor(
    private constructionDocumentsService: ConstructionDocumentsService,
    private resourcesService: ResourcesService,
    private queryParamService: QueryParamService,
    private translate: IpoTranslatePipe,
    private toastsService: ToastsService,
    private stringService: IpoStringService,
  ) {
    super();
  }

  ngOnInit(): void {
    setCurrentPath(this.path);
    this.checkIfs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !changes.path.firstChange &&
      changes.path.previousValue !== changes.path.currentValue
    ) {
      setCurrentPath(this.path);
      this.checkIfs();
    }
  }

  ngOnDestroy(): void {
    this.cancelSubs$.next();
  }

  checkIfs() {
    this.resourcesService
      .getSelectedResource$()
      ?.pipe(takeUntil(this.cancelSubs$))
      .subscribe((ifs) => {
        if (ifs) {
          this.createBreadcrumbs(ifs);
          this.getData(ifs.value);
          this.ifs$.next(ifs.value);
          this.noIfs$.next(false);
        } else {
          this.noIfs$.next(true);
        }
      });
  }

  getData(ifsNumber: string) {
    if (!this.path) {
      this.constructionDocumentsResponse$ =
        this.constructionDocumentsService.getPUBLFolder$(ifsNumber);
    } else {
      this.constructionDocumentsResponse$ =
        this.constructionDocumentsService.getFolderFromPath$(
          ifsNumber,
          this.path,
        );
    }
  }

  checkKnownError(error: Error) {
    if (
      error.message.startsWith(KnownErrorStates.NO_FOLDER_FOUND) ||
      error.message.startsWith(KnownErrorStates.NO_DRIVE_FOUND)
    ) {
      return true;
    } else {
      return false;
    }
  }

  getKnownErrorString(error: Error) {
    if (
      error.message.startsWith(KnownErrorStates.NO_DRIVE_FOUND) ||
      error.message.startsWith(KnownErrorStates.NO_FOLDER_FOUND)
    ) {
      return KnownErrorStates.NO_FILES_FOUND;
    }
  }

  handleFileDownload(item: ConstructionDocumentItem) {
    const ifs = this.ifs$.getValue();
    if (ifs.length && item) {
      this.constructionDocumentsService
        .getFile$(ifs, item.itemId)
        .pipe(takeUntil(this.cancelSubs$))
        .subscribe((res) => {
          if (!res.hasErrors) {
            res.data?.url && window.open(res.data.url, '_blank');
          } else {
            this.toastsService.addToast(
              {
                type: 'error',
                message: this.translate.transform('toast.errorReload'),
              },
              6000,
            );
          }
        });
    }
  }

  generateSizeString(data: ConstructionDocumentsResponse | undefined) {
    if (data) {
      return generateSizeString(
        data.currentCount,
        data.totalSizeText,
        this.stringService,
      );
    } else {
      return '';
    }
  }

  createBreadcrumbs(ifs: Resource) {
    const breadcrumbs: Breadcrumb[] = [this.createRootBreadcrumb(ifs)];
    if (this.path) {
      this.breadcrumbs$.next(
        breadcrumbs.concat(this.createBreadcrumbsFromPath()),
      );
    } else {
      this.breadcrumbs$.next(breadcrumbs);
    }
  }

  createRootBreadcrumb(ifs: Resource) {
    const breadcrumbName = `${ifs.name}_PUBL`;
    return { name: breadcrumbName, information: '' };
  }

  createBreadcrumbsFromPath() {
    const stringArray = this.path.split('/');
    let breadcrumbPath = '';
    const breadcrumbArray: Breadcrumb[] = [];
    stringArray.forEach((path, index) => {
      breadcrumbPath += index === 0 ? path : `/${path}`;
      breadcrumbArray.push({ name: path, information: breadcrumbPath });
    });
    return breadcrumbArray;
  }

  handleOnClickBreadcrumb(breadcrumb: Breadcrumb) {
    if (breadcrumb.information) {
      updateQueryPath(breadcrumb.information, this.queryParamService);
    } else {
      deleteQueryPath(this.queryParamService);
    }
  }

  handleOnClickSharepointExportButton() {
    window.open(
      'construction-documents/' + this.ifs$.getValue() + '/export',
      '_self',
    );
  }

  get hasAccessByRole() {
    return this.userService.checkAccess(SHAREPOINT_EXPORT_ROLES);
  }
}
